import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useParams, Link, useNavigate, useLocation } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  ArrowLeft,
  Pencil,
  Check,
  X,
  MapPin,
  Info,
  Tags,
  FileText,
  StickyNote,
} from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { getSurveysList, Survey, SurveyNote, DataTopicStatus, addSurveyNote } from "@/services/surveysListService"
import { getFilesForSource, GoogleDriveFile } from "@/services/biblioService"
import { FileButton } from "@/components/shared/FileButton"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import AddReportDialog from "@/components/survey-catalogue/AddReportDialog"
import UpdateReportDialog from "@/components/survey-catalogue/UpdateReportDialog"
import {
  useInlineDataTopicEdit,
  DATA_TOPIC_FIELDS,
  DATA_TOPIC_OPTIONS,
} from "@/hooks/useInlineDataTopicEdit"

/** Page title for this view — imported by the route to set document.title. */
export const PAGE_TITLE = "Survey Detail"

/**
 * Formats an ISO date string into a human-readable date (date only, no time).
 *
 * @param dateString - An ISO 8601 date string (e.g. "2025-06-01T12:34:56+00:00").
 * @returns A formatted date string like "1 Jun 2025", or the original string if parsing fails.
 */
function formatNoteDate(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    return dateString
  }
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function DetailSection({
  label,
  children,
}: {
  label: React.ReactNode
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  )
}

function DetailCard({
  icon,
  title,
  action,
  children,
  className = "",
}: {
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}): React.JSX.Element {
  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-primary" aria-hidden="true">
            {icon}
          </span>
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        </div>
        {action}
      </div>
      <div className={`p-4 ${className}`}>{children}</div>
    </section>
  )
}


export default function SurveyDetailPage(): React.JSX.Element {
  const { surveyId } = useParams<{ surveyId: string }>()
  const { getAccessToken, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const passedSurvey = (location.state as { survey?: Survey } | null)?.survey ?? null

  const [survey, setSurvey] = useState<Survey | null>(passedSurvey)
  const [loading, setLoading] = useState<boolean>(!passedSurvey)
  const [error, setError] = useState<string | null>(null)

  // Report file state
  const [reportFiles, setReportFiles] = useState<GoogleDriveFile[]>([])
  const [reportFilesLoading, setReportFilesLoading] = useState<boolean>(false)

  // Notes state
  const [noteText, setNoteText] = useState<string>("")
  const [noteSubmitting, setNoteSubmitting] = useState<boolean>(false)
  const [noteError, setNoteError] = useState<string | null>(null)

  /** Ref for the scrollable notes container to auto-scroll to bottom. */
  const notesContainerRef = useRef<HTMLDivElement>(null)


  const handleGoBack = useCallback((): void => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate("/survey-catalogue")
    }
  }, [navigate])

  /**
   * Callback passed to useInlineDataTopicEdit so it can trigger a data refresh
   * after saving data topic edits.
   */
  const handleDataTopicUpdated = useCallback((): void => {
    fetchSurveyRef.current()
  }, [])

  // Data topic inline editing hook
  const {
    editingRowKey: editingTopics,
    formValues: topicFormValues,
    submitting: topicSubmitting,
    submitError: topicSubmitError,
    openEdit: openTopicEdit,
    closeEdit: closeTopicEdit,
    handleFieldChange: handleTopicFieldChange,
    handleSave: handleTopicSave,
  } = useInlineDataTopicEdit(handleDataTopicUpdated)

  /** Stable ref to fetchSurvey so the callback doesn't cause dependency loops. */
  const fetchSurveyRef = useRef<() => Promise<void>>(() => Promise.resolve())

  /**
   * Fetches the survey from the API by loading the full list and filtering
   * by ID. Only called when the survey wasn't passed via route state, or
   * when refreshing data after adding a note.
   */
  const fetchSurvey = useCallback(async (): Promise<void> => {
    if (!surveyId) {
      setError("No survey ID provided")
      setLoading(false)
      return
    }

    try {
      const token = await getAccessToken()
      const surveys = await getSurveysList(token)
      const found = surveys.find((s) => s.mapSurveyId === surveyId)

      if (!found) {
        setError(`Survey with ID ${surveyId} not found`)
      } else {
        setSurvey(found)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load survey")
    } finally {
      setLoading(false)
    }
  }, [surveyId, getAccessToken])

  // Keep the ref in sync so the data topic hook can trigger a refresh
  fetchSurveyRef.current = fetchSurvey

  // Only fetch from the API if no survey was passed via route state
  useEffect(() => {
    if (!passedSurvey) {
      fetchSurvey()
    }
  }, [passedSurvey, fetchSurvey])

  /**
   * Load associated Google Drive report files when the survey is available.
   * Runs separately from the main fetch so it doesn't block the page render.
   */
  useEffect(() => {
    if (!survey?.reportBiblioId) return

    let cancelled = false

    async function loadReportFiles(): Promise<void> {
      setReportFilesLoading(true)
      try {
        const token = await getAccessToken()
        const filesResponse = await getFilesForSource(token, Number(survey!.reportBiblioId))
        if (!cancelled) {
          setReportFiles(filesResponse.files)
        }
      } catch {
        // Non-blocking — the page still renders without file thumbnails
        if (!cancelled) {
          setReportFiles([])
        }
      } finally {
        if (!cancelled) {
          setReportFilesLoading(false)
        }
      }
    }

    loadReportFiles()
    return () => { cancelled = true }
  }, [survey, getAccessToken])

  /** Notes sorted chronologically (oldest first, newest at the bottom). */
  const sortedNotes: SurveyNote[] = useMemo(() => {
    const notes = survey?.notes ?? []
    return [...notes].sort((a, b) => {
      return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    })
  }, [survey?.notes])

  // Scroll notes container to bottom when new notes arrive
  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight
    }
  }, [sortedNotes.length])

  /**
   * Handles submission of a new note for the current survey.
   * Calls the addSurveyNote service, then refreshes the survey data.
   */
  const handleAddNote = useCallback(async (): Promise<void> => {
    const trimmedNote = noteText.trim()
    if (!trimmedNote || noteSubmitting || !survey) return

    setNoteSubmitting(true)
    setNoteError(null)

    try {
      const token = await getAccessToken()
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"

      await addSurveyNote(token, {
        mapSurveyId: survey.mapSurveyId,
        note: trimmedNote,
        createdBy: createdBy,
      })

      setNoteText("")
      // Refresh survey data to pick up the new note
      await fetchSurvey()
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : "Failed to add note")
    } finally {
      setNoteSubmitting(false)
    }
  }, [noteText, noteSubmitting, survey, getAccessToken, user, fetchSurvey])

  if (loading) {
    return <LoadingSpinner loadingContent="Loading survey..." />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive font-medium">{error}</p>
        <Button variant="outline" asChild>
          <Link to="/survey-catalogue">
            <ArrowLeft data-icon="inline-start" />
            Back to Survey Catalogue
          </Link>
        </Button>
      </div>
    )
  }

  if (!survey) return <></>

  const pageTitle = `${survey.mapSurveyId} — ${PAGE_TITLE}`

  const hasParasiteRateMicroData: boolean =
    survey.rdt === DataTopicStatus.MICRO_DATA_EXTRACTED ||
    survey.microscopy === DataTopicStatus.MICRO_DATA_EXTRACTED

  return (
    <>
      <title>{pageTitle} - MAP Apps Platform</title>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none pt-0 ">
        <Button variant="ghost" className="size-7 w-fit mt-1 ml-2.5 mb-1" onClick={handleGoBack}>
          <ArrowLeft />
          Back to Survey Catalogue
        </Button>
        <CardHeader className="gap-4 border-b border-t bg-muted/30 -mt-6 pt-2 pb-0!">
          <div className="min-w-0 space-y-3">
            <CardTitle>
              <h1 className="text-2xl font-bold leading-tight tracking-tight text-foreground md:text-3xl max-w-6xl">
                {survey.title || survey.mapSurveyId}
              </h1>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {survey.countryName && (
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  <MapPin className="size-4 text-primary" />
                  {survey.countryName}
                </span>
              )}
              {survey.year && (
                <Badge variant="secondary" className="font-normal">
                  {survey.year}
                </Badge>
              )}
              {survey.surveyType && (
                <Badge variant="outline" className="font-normal">
                  {survey.surveyType}
                </Badge>
              )}
              {survey.populationRepresentativeness && (
                <Badge variant="outline" className="font-normal">
                  {survey.populationRepresentativeness}
                </Badge>
              )}
              {survey.surveyStatus && (
                <Badge variant="outline" className="font-normal">
                  {survey.surveyStatus}
                </Badge>
              )}
            </div>
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              MAP Survey ID: {survey.mapSurveyId}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0 overflow-y-auto">
          <div className="space-y-6 max-w-5xl">
            {/* Survey Information */}
            <DetailCard
              icon={<Info className="size-4" />}
              title="Survey Information"
              className="grid grid-cols-2 gap-4 sm:grid-cols-3"
            >
              <DetailSection label="MAP Survey ID">{survey.mapSurveyId}</DetailSection>
              <DetailSection label="Country">{survey.countryName || "—"}</DetailSection>
              <DetailSection label="Year">{survey.year || "—"}</DetailSection>
              <DetailSection label="Survey Type">{survey.surveyType || "—"}</DetailSection>
              {survey.populationRepresentativeness && (
                <DetailSection label="Targeted Population">
                  {survey.populationRepresentativeness}
                </DetailSection>
              )}
              <DetailSection label="Status">{survey.surveyStatus || "—"}</DetailSection>
              {survey.title && <DetailSection label="Title">{survey.title}</DetailSection>}
              {survey.dhsSurveyId && (
                <DetailSection label="DHS Survey ID">{survey.dhsSurveyId}</DetailSection>
              )}
              {survey.doi && <DetailSection label="DOI">{survey.doi}</DetailSection>}
              {survey.fieldworkStart && (
                <DetailSection label="Fieldwork Start">{survey.fieldworkStart}</DetailSection>
              )}
              {survey.fieldworkEnd && (
                <DetailSection label="Fieldwork End">{survey.fieldworkEnd}</DetailSection>
              )}
              {survey.implementingOrganisation && (
                <DetailSection label="Implementing Organisation">
                  {survey.implementingOrganisation}
                </DetailSection>
              )}
              {survey.phase && <DetailSection label="Phase">{survey.phase}</DetailSection>}
              {survey.surveyYearLabel && (
                <DetailSection label="Survey Year Label">{survey.surveyYearLabel}</DetailSection>
              )}
            </DetailCard>

            {/* Data Topics */}
            <DetailCard
              icon={<Tags className="size-4" />}
              title="Data Topics"
              className="space-y-4"
              action={
                editingTopics === null ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openTopicEdit(survey, survey.mapSurveyId)}
                  >
                    <Pencil className="size-3.5 mr-1.5" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => handleTopicSave(survey)}
                      disabled={topicSubmitting}
                    >
                      <Check className="size-3.5 mr-1.5" />
                      {topicSubmitting ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={closeTopicEdit}
                      disabled={topicSubmitting}
                    >
                      <X className="size-3.5 mr-1.5" />
                      Cancel
                    </Button>
                  </div>
                )
              }
            >
              {topicSubmitError && (
                <p className="text-xs text-destructive" role="alert">
                  {topicSubmitError}
                </p>
              )}
              {editingTopics !== null ? (
                /* Edit mode: dropdowns for each data topic */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DATA_TOPIC_FIELDS.map((field) => (
                    <div key={field.key} className="flex flex-col gap-1">
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {field.label}
                      </label>
                      <Select
                        value={topicFormValues[field.key]}
                        onValueChange={(value) =>
                          handleTopicFieldChange(field.key, value as DataTopicStatus)
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATA_TOPIC_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : (
                /* Read-only mode */
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <DetailSection label="Fever Treatment Seeking">
                    {survey.feverTreatmentSeeking}
                  </DetailSection>
                  <DetailSection label="Antimalarials">{survey.antimalarials}</DetailSection>
                  <DetailSection label="Chemoprevention">{survey.chemoprevention}</DetailSection>
                  <DetailSection label="ITN">{survey.itn}</DetailSection>
                  <DetailSection label="IRS">{survey.irs}</DetailSection>
                  <DetailSection label="Improved Housing">{survey.improvedHousing}</DetailSection>
                  <DetailSection label="RDT">{survey.rdt}</DetailSection>
                  <DetailSection label="Microscopy">{survey.microscopy}</DetailSection>
                </div>
              )}

              {/*
                Link to the parasite rate map, pre-filtered to just this survey
                via the `surveys` query param. Only shown when the survey has
                micro data extracted for RDT or Microscopy.
              */}
              {hasParasiteRateMicroData && (
                <Button variant="outline" size="sm" asChild className="w-fit">
                  <Link
                    to={`/data/parasite-rate/clusters?surveys=${encodeURIComponent(survey.mapSurveyId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${survey.mapSurveyId} on the parasite rate map (opens in a new tab)`}
                  >
                    <MapPin data-icon="inline-start" />
                    View on parasite rate map
                  </Link>
                </Button>
              )}
            </DetailCard>

            {/* Report File */}
            <DetailCard
              icon={<FileText className="size-4" />}
              title="Report File"
              action={
                survey.reportBiblioFile ? (
                  <UpdateReportDialog onReportCreated={fetchSurvey} survey={survey} />
                ) : (
                  <AddReportDialog onReportCreated={fetchSurvey} survey={survey} />
                )
              }
            >
              {reportFilesLoading ? (
                /* Show the direct link immediately while Drive metadata loads */
                survey.reportBiblioFile ? (
                  <a
                    href={survey.reportBiblioFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
                  >
                    Open report file
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">Loading report files…</p>
                )
              ) : reportFiles.length > 0 ? (
                <div className="flex gap-3 flex-wrap">
                  {reportFiles.map((file) => (
                    <FileButton key={file.id} file={file} />
                  ))}
                </div>
              ) : survey.reportBiblioFile ? (
                /* Fallback: direct link if file metadata couldn't be loaded */
                <a
                  href={survey.reportBiblioFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  Open report file
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No report file linked to this survey.
                </p>
              )}
            </DetailCard>

            {/* Comments / Notes */}
            <DetailCard
              icon={<StickyNote className="size-4" />}
              title="Comments"
              className="space-y-3"
            >
              {/* Scrollable notes list */}
              <div
                ref={notesContainerRef}
                className="max-h-[500px] overflow-y-auto"
              >
                {sortedNotes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No comments for this survey yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sortedNotes.map((note) => (
                      <div
                        key={note.createdOn}
                        className="rounded-md border bg-muted/20 p-3 text-sm"
                      >
                        <p className="whitespace-pre-wrap">{note.note}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <span className="truncate min-w-0">{note.createdBy}</span>
                          <span className="ml-auto shrink-0">
                            {formatNoteDate(note.createdOn)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add note form */}
              <Textarea
                placeholder="Write a comment…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                disabled={noteSubmitting}
                className="min-h-[80px] resize-none"
                aria-label="New comment"
              />
              {noteError && (
                <p className="text-xs text-destructive" role="alert">
                  {noteError}
                </p>
              )}
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={noteSubmitting || !noteText.trim()}
              >
                {noteSubmitting ? "Adding…" : "Add Comment"}
              </Button>
            </DetailCard>
          </div>
        </CardContent>
      </Card>
    </>
  )
}
